/**
 * UAEPlate — live-rendered UAE car plate designs (12 official designs).
 * Built from the designer's HTML/CSS (uae-plates.css) — NOT static images,
 * so the code & number are fully writable.
 *
 * Props:
 *   emirate  : abu_dhabi | dubai | sharjah | ajman | umm_al_quwain | ras_al_khaimah | fujairah
 *   category : private | motorcycle | classic
 *   code     : plate category code (e.g. "5", "AA", "B")
 *   number   : plate number (e.g. "30026") — optional
 *   width    : rendered width in px (auto-scales, default 420)
 */
import './uae-plates.css';

const A = 'https://asb606.com/plates'; // CDN — single source of truth (managed by ASB606 admin)

// (emirate:category) combos that have a real designer plate
const DESIGNS = new Set([
  'abu_dhabi:private',
  'dubai:private', 'dubai:motorcycle', 'dubai:classic',
  'sharjah:private',
  'ajman:private',
  'umm_al_quwain:private', 'umm_al_quwain:motorcycle',
  'ras_al_khaimah:private', 'ras_al_khaimah:motorcycle', 'ras_al_khaimah:classic',
  'fujairah:private',
]);

export const hasPlateDesign = (emirate, category) => DESIGNS.has(`${emirate}:${category}`);

// base (unscaled) dimensions per shell type
const DIMS = {
  long: { w: 420, h: 96 },
  bike: { w: 480, h: 278 },
  rakClassic: { w: 420, h: 205 },
};

function Shell({ base, width, children, testId }) {
  const scale = width / base.w;
  return (
    <div
      className="uae-plate"
      dir="ltr"
      style={{ width, height: Math.round(base.h * scale), overflow: 'hidden' }}
      data-testid={testId}
    >
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: base.w, height: base.h }}>
        {children}
      </div>
    </div>
  );
}

export default function UAEPlate({ emirate, category = 'private', code = '', number = '', width = 420 }) {
  const key = `${emirate}:${category}`;
  if (!DESIGNS.has(key)) return null;
  const num = number || '';

  // ─── Abu Dhabi (private) ───
  if (key === 'abu_dhabi:private') {
    return (
      <Shell base={DIMS.long} width={width} testId="plate-abu_dhabi-private">
        <div className="plate-long">
          <div className="ad-red-strip"><span className="code-text">{code}</span></div>
          <div className="ad-logo-zone">
            <img className="shield" src={`${A}/ad-shield.png`} alt="" />
            <img className="script" src={`${A}/ad-script.png`} alt="" />
          </div>
          <span className="zone zone-label">
            <span className="ar">الامارات</span>
            <span className="ar">أبوظبي</span>
            <span className="en">U.A.E A.D</span>
          </span>
          <span className="zone zone-number"><span className="num-text">{num}</span></span>
        </div>
      </Shell>
    );
  }

  // ─── Dubai (private / car) ───
  if (key === 'dubai:private') {
    return (
      <Shell base={DIMS.long} width={width} testId="plate-dubai-private">
        <div className="plate-long">
          <div className="dubai-stack-zone">
            <img className="dxb-logo" src={`${A}/dxb-logo.png`} alt="DUBAI" />
            <span className="dxb-code">{code}</span>
          </div>
          <span className="zone zone-number"><span className="num-text">{num}</span></span>
        </div>
      </Shell>
    );
  }

  // ─── Dubai motorbike ───
  if (key === 'dubai:motorcycle') {
    return (
      <Shell base={DIMS.bike} width={width} testId="plate-dubai-motorcycle">
        <div className="plate-bike">
          <div className="bq bq-code"><span className="bcode">{code}</span></div>
          <div className="bq bq-icon"><img src={`${A}/bike-icon.png`} alt="motorbike" /></div>
          <div className="bq bq-wm">
            <img className="wm-en" src={`${A}/dxb-wm-en.png`} alt="DUBAI" />
            <img className="wm-ar" src={`${A}/dxb-wm-ar.png`} alt="دبي" />
          </div>
          <div className="bq bq-num"><span className="bnum">{num}</span></div>
        </div>
      </Shell>
    );
  }

  // ─── Dubai classic (no code zone — the number IS the identity) ───
  if (key === 'dubai:classic') {
    return (
      <Shell base={DIMS.long} width={width} testId="plate-dubai-classic">
        <div className="plate-long">
          <div className="dubai-classic-zone"><img src={`${A}/dubai-classic.jpg`} alt="DUBAI CLASSIC" /></div>
          <span className="zone zone-number"><span className="num-text">{num || code}</span></span>
        </div>
      </Shell>
    );
  }

  // ─── Sharjah ───
  if (key === 'sharjah:private') {
    return (
      <Shell base={DIMS.long} width={width} testId="plate-sharjah-private">
        <div className="plate-long sharjah">
          <span className="zone zone-uae-vert">U<br />A<br />E</span>
          <span className="zone zone-code"><span className="code-text">{code}</span></span>
          <span className="zone zone-label">
            <span className="big">الشارقة</span>
            <span className="en" style={{ fontSize: 13, fontFamily: "'Baloo 2', Arial, sans-serif" }}>Sharjah</span>
          </span>
          <span className="zone zone-number"><span className="num-text">{num}</span></span>
        </div>
      </Shell>
    );
  }

  // ─── Ajman ───
  if (key === 'ajman:private') {
    return (
      <Shell base={DIMS.long} width={width} testId="plate-ajman-private">
        <div className="plate-long">
          <span className="zone zone-code"><span className="code-text">{code}</span></span>
          <span className="zone zone-number"><span className="num-text">{num}</span></span>
          <span className="ajman-logo-zone">
            <img className="flag-img" src={`${A}/uae-flag.png`} alt="UAE Flag" />
            <img className="ajman-word" src={`${A}/ajman-word.png`} alt="AJMAN" />
          </span>
        </div>
      </Shell>
    );
  }

  // ─── Umm Al Quwain ───
  if (key === 'umm_al_quwain:private') {
    return (
      <Shell base={DIMS.long} width={width} testId="plate-umm_al_quwain-private">
        <div className="plate-long">
          <span className="zone zone-code"><span className="code-text">{code}</span></span>
          <span className="uaq-logo-zone">
            <img src={`${A}/uaq-logo-small.png`} alt="Umm Al Quwain" />
          </span>
          <span className="zone zone-number"><span className="num-text">{num}</span></span>
        </div>
      </Shell>
    );
  }

  // ─── UAQ motorbike ───
  if (key === 'umm_al_quwain:motorcycle') {
    return (
      <Shell base={DIMS.bike} width={width} testId="plate-umm_al_quwain-motorcycle">
        <div className="plate-bike">
          <div className="bq bq-uaq-logo">
            <img src={`${A}/uaq-logo-big.png`} alt="Umm Al Quwain" />
          </div>
          <div className="bq bq-icon"><img src={`${A}/bike-icon2.png`} alt="motorbike" /></div>
          <div className="bq bq-uaq-code"><span className="bcode">{code}</span></div>
          <div className="bq bq-uaq-num"><span className="bnum">{num}</span></div>
        </div>
      </Shell>
    );
  }

  // ─── Ras Al Khaimah ───
  if (key === 'ras_al_khaimah:private') {
    return (
      <Shell base={DIMS.long} width={width} testId="plate-ras_al_khaimah-private">
        <div className="plate-long">
          <span className="rak-logo-zone"><img src={`${A}/rak-logo.png`} alt="رأس الخيمة" /></span>
          <span className="zone zone-code"><span className="code-text">{code}</span></span>
          <span className="zone zone-number"><span className="num-text">{num}</span></span>
        </div>
      </Shell>
    );
  }

  // ─── RAK motorbike ───
  if (key === 'ras_al_khaimah:motorcycle') {
    return (
      <Shell base={DIMS.bike} width={width} testId="plate-ras_al_khaimah-motorcycle">
        <div className="plate-bike">
          <div className="bq bq-code"><span className="bcode">{code}</span></div>
          <div className="bq bq-icon"><img src={`${A}/rak-logo.png`} alt="رأس الخيمة" /></div>
          <div className="bq bq-rak-row">
            <span className="rak-wm-en">U.A.E.</span>
            <span className="bnum">{num}</span>
            <span className="rak-wm-ar">الامارات</span>
          </div>
        </div>
      </Shell>
    );
  }

  // ─── RAK classic (no code zone) ───
  if (key === 'ras_al_khaimah:classic') {
    return (
      <Shell base={DIMS.rakClassic} width={width} testId="plate-ras_al_khaimah-classic">
        <div className="plate-rak-classic">
          <span className="rc-el rc-rak">RAK</span>
          <span className="rc-el rc-logo-wrap"><img src={`${A}/rak-classic-logo.png`} alt="Classic" /></span>
          <span className="rc-el rc-ar">رأس الخيمة</span>
          <div className="rc-el rc-num">{num || code}</div>
        </div>
      </Shell>
    );
  }

  // ─── Fujairah ───
  if (key === 'fujairah:private') {
    return (
      <Shell base={DIMS.long} width={width} testId="plate-fujairah-private">
        <div className="plate-long">
          <span className="zone zone-code"><span className="code-text">{code}</span></span>
          <span className="zone fujairah-logo-zone">
            <img src={`${A}/fujairah-logo.png`} alt="FUJAIRAH" />
          </span>
          <span className="zone zone-number"><span className="num-text">{num}</span></span>
        </div>
      </Shell>
    );
  }

  return null;
}
